import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import { endOfMonth, startOfMonth, subMonths } from 'date-fns';
import React from 'react';
import { useForm } from 'react-hook-form';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import { isDateEarlierThan, isDateLaterThan } from 'utils/validators';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

interface DateRangeForm {
    dateFrom: string;
    dateTo: string;
}

interface DateRangePickerModalProps {
    onCancel: () => void;
    onSave: (dateFrom: string, dateTo: string) => void;
    initialDateFrom?: string;
    initialDateTo?: string;
    saveButtonTextId?: string;
    maxDateTo?: Date;
    /** When true, both date fields are optional but at least one must be filled */
    atLeastOneRequired?: boolean;
    /** When true, shows a "Previous month" quick pick that prefills the previous month's first and last day */
    showPreviousMonthQuickPick?: boolean;
}

const DateRangePickerModal: React.FC<DateRangePickerModalProps> = ({
    onCancel,
    onSave,
    initialDateFrom = '',
    initialDateTo = '',
    saveButtonTextId = 'apply',
    maxDateTo,
    atLeastOneRequired = false,
    showPreviousMonthQuickPick = false,
}) => {
    const { control, handleSubmit, trigger, setValue } = useForm<DateRangeForm>({
        mode: 'onChange',
        defaultValues: {
            dateFrom: initialDateFrom,
            dateTo: initialDateTo,
        },
    });

    const atLeastOneRequiredMessage = t('validation.requires-at-least-one-of-fields', {
        fields: [`“${t('time.start-date')}”`, `“${t('time.end-date')}”`].join(` ${t('or')} `),
    });

    const onSubmit = (data: DateRangeForm) => {
        onSave(data.dateFrom, data.dateTo);
    };

    const prefillPreviousMonth = () => {
        const previousMonth = subMonths(new Date(), 1);
        setValue('dateFrom', formatDateStringInput(startOfMonth(previousMonth)), {
            shouldValidate: true,
            shouldDirty: true,
        });
        setValue('dateTo', formatDateStringInput(endOfMonth(previousMonth)), {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    return (
        <>
            <ModalHeader title={t('time.pick-a-specific-date-range')} onCancel={onCancel} />
            <div className="p-6">
                <div className="flex flex-row gap-4">
                    <div className="min-w-0 flex-1">
                        <DateInput
                            control={control}
                            name="dateFrom"
                            label="time.start-date"
                            required={!atLeastOneRequired}
                            fullWidth
                            onChange={() => {
                                trigger('dateTo');
                            }}
                            validate={(value, formValues) => {
                                if (atLeastOneRequired && !value && !formValues.dateTo) {
                                    return atLeastOneRequiredMessage;
                                }
                                if (formValues.dateTo && value) {
                                    return isDateEarlierThan(value, formValues.dateTo, t('time.end-date'));
                                }
                            }}
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <DateInput
                            control={control}
                            name="dateTo"
                            label="time.end-date"
                            required={!atLeastOneRequired}
                            fullWidth
                            maxDate={maxDateTo}
                            onChange={() => {
                                trigger('dateFrom');
                            }}
                            validate={(value, formValues) => {
                                if (atLeastOneRequired && !value && !formValues.dateFrom) {
                                    return atLeastOneRequiredMessage;
                                }
                                if (formValues.dateFrom && value) {
                                    return isDateLaterThan(value, formValues.dateFrom, t('time.start-date'));
                                }
                            }}
                        />
                    </div>
                </div>
                {showPreviousMonthQuickPick ? (
                    <div className="mt-2 flex flex-wrap gap-3">
                        <Button
                            {...buttonStyle(
                                { variant: 'text' },
                                'text-primary h-[22px] w-fit min-w-0 p-0 font-normal hover:underline'
                            )}
                            onClick={prefillPreviousMonth}
                        >
                            {t('time.previous-month')}
                        </Button>
                    </div>
                ) : null}
            </div>
            <ModalFooter
                onCancel={onCancel}
                onSave={() => {
                    void handleSubmit(onSubmit)();
                }}
                saveButtonTextId={saveButtonTextId}
                cancelButtonTextId="cancel"
            />
        </>
    );
};

export default DateRangePickerModal;
